/**
 * StickyChat - Floating AI Assistant chat widget
 * A mini version of the AI Assistant that appears on every page
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  CircularProgress,
  Grow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  aiAssistantApi,
  ChatMessage,
  ConversationHistory,
} from '../../modules/ai-crm/api/aiAssistantApi';
import { usePermissions } from '../../hooks/usePermissions';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'assistant',
  content:
    "Hi! I'm your AI Assistant. Ask me anything about your leads, tasks, or clients.",
  timestamp: new Date(),
};

const StickyChat: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Permission check
  if (!hasPermission('ai_assistant:use')) {
    return null;
  }

  // Fetch conversations list (only when widget is first opened)
  const { data: conversations } = useQuery({
    queryKey: ['ai-conversations'],
    queryFn: () => aiAssistantApi.getConversations(),
    enabled: open && !hasLoadedHistory,
  });

  // Load most recent conversation when conversations are fetched
  const loadHistoryMutation = useMutation({
    mutationFn: (convId: string) =>
      aiAssistantApi.getConversationHistory(convId),
    onSuccess: (data: ConversationHistory) => {
      const loadedMessages: Message[] = [];
      data.messages.forEach((msg) => {
        loadedMessages.push({
          id: `user-${msg.id}`,
          role: 'user',
          content: msg.userMessage,
          timestamp: new Date(msg.timestamp),
        });
        loadedMessages.push({
          id: `assistant-${msg.id}`,
          role: 'assistant',
          content: msg.aiResponse,
          timestamp: new Date(msg.timestamp),
        });
      });
      if (loadedMessages.length > 0) {
        setMessages(loadedMessages);
      }
      setConversationId(data.conversationId);
      setHasLoadedHistory(true);
    },
    onError: () => {
      setHasLoadedHistory(true);
    },
  });

  // Load most recent conversation on first open
  useEffect(() => {
    if (
      open &&
      !hasLoadedHistory &&
      conversations &&
      conversations.length > 0 &&
      !loadHistoryMutation.isPending
    ) {
      loadHistoryMutation.mutate(conversations[0].conversationId);
    } else if (open && !hasLoadedHistory && conversations && conversations.length === 0) {
      setHasLoadedHistory(true);
    }
  }, [open, conversations, hasLoadedHistory]);

  // Chat mutation
  const chatMutation = useMutation({
    mutationFn: (message: string) =>
      aiAssistantApi.chat({
        message,
        conversationId: conversationId || undefined,
      }),
    onSuccess: (response: ChatMessage) => {
      if (!conversationId) {
        setConversationId(response.conversationId);
      }

      const assistantMessage: Message = {
        id: response.id,
        role: 'assistant',
        content: response.response,
        timestamp: new Date(response.createdAt),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Refresh conversations list
      queryClient.invalidateQueries({ queryKey: ['ai-conversations'] });
    },
    onError: () => {
      // Remove the pending user message on error
      setMessages((prev) => prev.slice(0, -1));
    },
  });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || chatMutation.isPending) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate(trimmed);
    setInputValue('');
  }, [inputValue, chatMutation]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenFull = () => {
    setOpen(false);
    navigate('/ai-crm/ai-assistant');
  };

  // Format message content (basic markdown bold)
  const formatContent = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <>
      {/* Expanded chat panel */}
      <Grow in={open} style={{ transformOrigin: 'bottom right' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 88,
            right: isMobile ? 0 : 24,
            width: isMobile ? '100%' : 380,
            height: isMobile ? '100%' : 520,
            zIndex: 1300,
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BotIcon fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                AI Assistant
              </Typography>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={handleOpenFull}
                sx={{ color: 'inherit', mr: 0.5 }}
                title="Open full AI Assistant"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: 'inherit' }}
                title="Minimize"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {loadHistoryMutation.isPending && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}

            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent:
                    message.role === 'user' ? 'flex-end' : 'flex-start',
                  gap: 0.5,
                }}
              >
                {message.role === 'assistant' && (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <BotIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}

                <Paper
                  elevation={0}
                  sx={{
                    px: 1.5,
                    py: 1,
                    maxWidth: '80%',
                    bgcolor:
                      message.role === 'user'
                        ? 'primary.main'
                        : 'background.paper',
                    color:
                      message.role === 'user'
                        ? 'primary.contrastText'
                        : 'text.primary',
                    borderRadius: 2,
                    borderTopLeftRadius:
                      message.role === 'assistant' ? 0 : 16,
                    borderTopRightRadius: message.role === 'user' ? 0 : 16,
                    border:
                      message.role === 'assistant'
                        ? '1px solid'
                        : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      '& strong': { fontWeight: 600 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatContent(message.content),
                    }}
                  />
                </Paper>

                {message.role === 'user' && (
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'grey.400',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}
              </Box>
            ))}

            {/* Typing indicator */}
            {chatMutation.isPending && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BotIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        bgcolor: 'grey.400',
                        borderRadius: '50%',
                        animation: 'stickyTyping 1s infinite',
                      }}
                    />
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        bgcolor: 'grey.400',
                        borderRadius: '50%',
                        animation: 'stickyTyping 1s infinite 0.2s',
                      }}
                    />
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        bgcolor: 'grey.400',
                        borderRadius: '50%',
                        animation: 'stickyTyping 1s infinite 0.4s',
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input area */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              p: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              placeholder="Type a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={chatMutation.isPending}
              size="small"
              multiline
              maxRows={3}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: 13,
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() || chatMutation.isPending}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 36,
                height: 36,
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
              }}
            >
              {chatMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Paper>
      </Grow>

      {/* FAB button */}
      <Fab
        color="primary"
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          width: 56,
          height: 56,
          display: open && isMobile ? 'none' : 'flex',
        }}
        aria-label="AI Assistant"
      >
        {open ? <CloseIcon /> : <BotIcon />}
      </Fab>

      {/* Typing animation keyframes */}
      <style>
        {`
          @keyframes stickyTyping {
            0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
            30% { transform: translateY(-3px); opacity: 1; }
          }
        `}
      </style>
    </>
  );
};

export default StickyChat;
